<template>
  <div>
    <v-container fluid class="pa-6">
      <!-- Título -->
      <div class="d-flex align-center mb-4">
        <v-icon color="primary" class="mr-3" size="32"
          >mdi-file-document</v-icon
        >
        <span class="text-h4 font-weight-bold">Generar Reporte</span>
      </div>

      <!-- Breadcrumbs -->
      <v-breadcrumbs :items="bread" class="mb-4 pa-0"></v-breadcrumbs>
      <v-divider class="mb-6"></v-divider>

      <!-- Reportes Especiales Component -->
      <ReportSpecialSection
        ref="reportSpecialSection"
        :tipos-reportes="tiposReportes"
        :loading="isLoading"
        :is-exporting="isExporting"
        :reporte-generado="reporteGenerado"
        @generar-reporte-especial="generarReporteEspecial"
        @configure-special-filters="configurarFiltrosEspeciales"
      />

      <!-- Filtros Component -->
      <ReportFiltersSection
        :fideicomisos="fideicomisos"
        :tipos-servicios-filtrados="tiposServiciosFiltrados"
        :operatorias-disponibles="operatoriasDisponibles"
        :solicitud-estados="solicitudEstados"
        :tipos-persona="tiposPersona"
        :departamentos="departamentos"
        :filtros-activos="filtrosActivos"
        :puede-generar-reporte="puedeGenerarReporte"
        :filters-disabled="filtrosDeshabilitados"
        :es-operacion="esOperacion"
        :loading="isLoading"
        :reporte-especial-activo="reporteEspecialActivo"
        :filtro-fideicomiso-parent="filtroFideicomiso"
        :filtro-tipo-servicio-parent="filtroTipoServicio"
        :filtro-operatoria-parent="filtroOperatoria"
        :estado-seleccionado-parent="estadoSeleccionado"
        :filtro-tipo-persona-parent="filtroTipoPersona"
        :filtro-departamento-parent="filtroDepartamento"
        :fecha-inicio-parent="fechaInicio"
        :fecha-final-parent="fechaFinal"
        @fideicomiso-change="handleFideicomisoChange"
        @tipo-servicio-change="handleTipoServicioChange"
        @operatoria-change="handleOperatoriaChange"
        @estado-change="handleEstadoChange"
        @departamento-change="handleDepartamentoChange"
        @remove-fideicomiso="removeFideicomiso"
        @remove-operatoria="removeOperatoria"
        @remove-estado="removeEstado"
        @remove-filter="removeFilter"
        @limpiar-filtros="limpiarFiltros"
        @generar-reporte="activarTabla"
        @subtotales-change="handleSubtotalesChange"
        @update:filtro-fideicomiso="filtroFideicomiso = $event"
        @update:filtro-tipo-servicio="filtroTipoServicio = $event"
        @update:filtro-operatoria="filtroOperatoria = $event"
        @update:estado-seleccionado="estadoSeleccionado = $event"
        @update:filtro-tipo-persona="filtroTipoPersona = $event"
        @update:filtro-departamento="filtroDepartamento = $event"
        @update:fecha-inicio="fechaInicio = $event"
        @update:fecha-final="fechaFinal = $event"
      />
    </v-container>
    <TablaReportes
      v-if="mostrarTabla"
      :resultados="tablaMockeada.resultados"
      :total-items="totalItems"
      :items-por-pagina="itemsPorPagina"
      :pagina-actual="paginaActual"
      :loading="isLoading"
      :is-exporting="isExporting"
      :is-exporting-excel="isExportingExcel"
      :is-exporting-in-background="isExportingInBackground"
      :is-exporting-pdf-in-background="isExportingPdfInBackground"
      :headers="headersTabla"
      :es-operacion="esOperacion"
      :totales="tablaMockeada.totales"
      :fecha-inicio="fechaInicio"
      :fecha-final="fechaFinal"
      :periodo-saldos-htc="periodoSaldosHTC"
      @cambiar-pagina="cambiarPagina"
      @cambiar-items-por-pagina="cambiarItemsPorPagina"
      @exportar-datos="manejarExportacion"
      ref="tablaReportes"
    />

    <!-- Progress Dialog para SSE -->
    <ProgressDialog
      :show="showProgressDialog"
      :progress="progressData.progress"
      :processed-records="progressData.processedRecords"
      :total-records="progressData.totalRecords"
      :estimated-time-left="progressData.estimatedTimeLeft"
      :status-message="progressData.statusMessage"
      :status-type="progressData.statusType"
      @cancel="cancelProgress"
    />

    <!-- Loading Dialog para exportaciones -->
    <LoadingDialog
      :show="showLoadingDialog"
      :title="loadingTitle"
      :message="loadingMessage"
      @cancel="cancelarExportacion"
      @background="continuarEnSegundoPlano"
    />
  </div>
</template>

<script>
import { showSimpleMessage } from '@/common/util';
import TablaReportes from '@/components/Reportes/TablaReportes.vue';
import ReportSpecialSection from '@/components/Reportes/ReportSpecialSection.vue';
import ReportFiltersSection from '@/components/Reportes/ReportFiltersSection.vue';
import ProgressDialog from '@/components/Reportes/ProgressDialog.vue';
import LoadingDialog from '@/components/Reportes/LoadingDialog.vue';
import reportesApiService from '@/utils/reportesApi';
import reportesExportService from '@/utils/reportesExport';
import reportesCache from '@/utils/reportesCache';
import { ReportesHelpers } from '@/utils/reportesHelpers';
import reportesEspecialesService from '@/services/reportesEspecialesService';

export default {
  components: {
    TablaReportes,
    ReportSpecialSection,
    ReportFiltersSection,
    ProgressDialog,
    LoadingDialog,
  },
  name: 'GenerarReportes',

  data: () => ({
    page: 1,
    limit: 10,

    informes: [],
    informesPDF: [],
    tiposServicios: [],
    filtroTipoServicio: null,
    sectoresProductivos: [],
    // filtroSectorProductivo: 'todas',
    fideicomisosId: '',
    tiposServiciosId: '',
    listaOperatorias: [],
    fideicomisos: [],
    operatorias: [{ id: 'todas', nombre: 'Todas' }],
    listaParametros: [],
    selectedParams: [],
    estadoSeleccionado: [],
    estadosSolicitud: [],
    solicitudEstados: [],
    estadosPermitidos: [
      'Precalificación',
      'Calificación',
      'Análisis',
      'Requerimiento',
      'Desistido',
      'Aprobado',
      'Otorgado',
      'Cancelado',
      'Rechazado',
    ],
    tiposOperatorias: ['Financiamiento', 'ANR', 'Mendoza Activa'],
    filtroTipoPersona: 'Ambas',
    tiposPersona: ['Física', 'Jurídica', 'Ambas'],
    operatoria: '',
    fechaInicio: '2020-01-01',
    fechaFinal: new Date().toISOString().substr(0, 10),
    filtroOperatoria: [],
    filtroFideicomiso: [],
    filtroDepartamento: [],
    departamentos: [],
    resultadosReporte: [],
    tiposReportes: [
      'Info Mora (Financiamiento)',
      'Info Cobranzas Proyectadas',
      'Info Cobranzas',
      'Info Cte y No Cte',
      // 'Reporte de Saldos - HTC', // Oculto momentáneamente
    ],

    tablaMockeada: {
      statusCode: null,
      timestamp: null,
      status: null,
      response: {
        pagination: {},
        resultados: [],
        total: 0,
      },
    },

    mostrarTabla: false,

    // Paginación
    paginaActual: 1,
    itemsPorPagina: 10,
    totalItems: 0,

    bread: [
      {
        text: 'Mendoza Fiduciaria',
        disabled: false,
        href: '/',
      },
      {
        text: 'Dashboard',
        disabled: true,
        href: '/',
      },
    ],

    isLoading: false,
    reporteEspecialActivo: null,
    configuracionEspecial: null,
    mostrarSubtotales: false,
    fechaDisponibleCteYNoCte: null, // Fecha de corte para Info Cte y No Cte
    fechaDisponibleSaldosHTC: null, // Fecha de corte para Reporte de Saldos - HTC
    periodoSaldosHTC: null, // Periodo del reporte HTC (formato: "2025-06-30/2025-09-30")
    reporteGenerado: false, // Indica si el reporte ha sido generado

    // SSE Progress Dialog
    showProgressDialog: false,
    progressCancelled: false,
    sseJobActive: false, // Flag para evitar abrir/cerrar barra múltiples veces
    progressData: {
      progress: 0,
      processedRecords: 0,
      totalRecords: 0,
      estimatedTimeLeft: 0,
      statusMessage: '',
      statusType: 'info'
    },

    // Loading Dialog para exportaciones
    showLoadingDialog: false,
    loadingTitle: 'Exportando a Excel',
    loadingMessage: 'Generando archivo de exportación...',
    exportacionCancelada: false,
    exportacionEnSegundoPlano: false,
    isExporting: false,
    isExportingExcel: false,
    isExportingInBackground: false,
    isExportingPdfInBackground: false,
  }),

  computed: {
    tiposServiciosFiltrados() {
      return this.tiposServicios
        .filter((servicio) =>
          servicio.id !== 'todas' && servicio.nombre !== 'Mendoza Activa'
        )
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
    },
    operatoriasFiltradas() {
      return this.operatorias
        .filter((operatoria) => operatoria.id !== 'todas')
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
    },
    operatoriasDisponibles() {
      // Para reportes especiales (como Info Mora), mostrar operatorias si están disponibles
      // independientemente del tipo de servicio
      if (this.reporteEspecialActivo) {
        return this.operatoriasFiltradas;
      }

      // Mostrar operatorias si hay tipo de servicio seleccionado (flujo normal)
      const tieneTipoServicio = this.filtroTipoServicio;

      if (tieneTipoServicio) {
        return this.operatoriasFiltradas;
      }
      return [];
    },
    filtrosActivos() {
      return ReportesHelpers.construirFiltrosActivos(this);
    },
    puedeGenerarReporte() {
      return ReportesHelpers.validarGeneracionReporte(
        this.filtroTipoServicio,
        this.filtroOperatoria,
        this.reporteEspecialActivo
      );
    },
    headersTabla() {
      const headers = ReportesHelpers.obtenerHeadersTabla(
        this.tiposServicios,
        this.filtroTipoServicio,
        this.reporteEspecialActivo
      );
      if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
        console.log('🔍 Headers para Info Cte y No Cte:', headers);
        console.log('🔍 Headers específicos monto_otorgado:', headers.find(h => h.value === 'monto_otorgado'));
      }
      return headers;
    },
    esOperacion() {
      if (this.reporteEspecialActivo === 'Info Mora (Financiamiento)') {
        return 'Info Mora (Financiamiento)';
      }
      if (this.reporteEspecialActivo === 'Info Cobranzas Proyectadas') {
        return 'Info Cobranzas Proyectadas';
      }
      if (this.reporteEspecialActivo === 'Info Cobranzas') {
        return 'Info Cobranzas';
      }
      if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
        return 'Info Cte y No Cte';
      }
      if (this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
        return 'Reporte de Saldos - HTC';
      }
      return ReportesHelpers.obtenerTipoOperacion(
        this.tiposServicios,
        this.filtroTipoServicio
      );
    },
    filtrosDeshabilitados() {
      const disabled = {
        tipoServicio: false,
        estadoSolicitud: false,
        fideicomiso: false,
        operatoria: false,
        tipoPersona: false,
        departamento: false,
        fechaDesde: false,
        fechaHasta: false,
      };

      // Si el reporte ya fue generado, deshabilitar todos los filtros
      if (this.reporteGenerado) {
        return {
          tipoServicio: true,
          estadoSolicitud: true,
          fideicomiso: true,
          operatoria: true,
          tipoPersona: true,
          departamento: true,
          fechaDesde: true,
          fechaHasta: true,
        };
      }

      if (this.reporteEspecialActivo === 'Info Mora (Financiamiento)') {
        disabled.tipoServicio = true;
        disabled.estadoSolicitud = true;
      }

      if (this.reporteEspecialActivo === 'Info Cobranzas Proyectadas') {
        disabled.tipoServicio = true;
        disabled.estadoSolicitud = true;
        disabled.fechaDesde = true;
        // fechaHasta permanece false (habilitado)
      }

      if (this.reporteEspecialActivo === 'Info Cobranzas') {
        disabled.tipoServicio = true;
        disabled.estadoSolicitud = true;
        // fechaDesde y fechaHasta permanecen false (habilitados)
      }

      if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
        disabled.tipoServicio = true; // Deshabilitar tipo de operatoria
        disabled.estadoSolicitud = true;
        disabled.operatoria = false; // Habilitar operatoria
        disabled.fideicomiso = false; // Habilitar fideicomiso
        disabled.fechaDesde = true;
        disabled.fechaHasta = true;
      }

      if (this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
        disabled.tipoServicio = true; // Deshabilitar tipo de operatoria
        disabled.estadoSolicitud = true;
        disabled.operatoria = false; // Habilitar operatoria
        disabled.fideicomiso = false; // Habilitar fideicomiso
        disabled.fechaDesde = true;
        disabled.fechaHasta = true;
      }

      return disabled;
    },
    camposResumen() {
      return [
        {
          id: 'tipoServicio',
          etiqueta: 'Tipo de Servicio',
          valor: this.getNombreFiltro('tipoServicio'),
        },
        {
          id: 'sectorProductivo',
          etiqueta: 'Sector Productivo',
          valor: this.getNombreFiltro('sectorProductivo'),
        },
        {
          id: 'fideicomiso',
          etiqueta: 'Fideicomiso',
          valor: this.getNombreFiltro('fideicomiso'),
        },
        {
          id: 'operatoria',
          etiqueta: 'Operatoria',
          valor: this.getNombreFiltro('operatoria'),
        },
        {
          id: 'estadoSolicitud',
          etiqueta: 'Estado de Solicitud',
          valor: this.getNombreFiltro('estadoSolicitud'),
        },
        {
          id: 'tipoPersona',
          etiqueta: 'Tipo de Persona',
          valor: this.getNombreFiltro('tipoPersona'),
        },
        { id: 'fechaInicio', etiqueta: 'Fecha Desde', valor: this.fechaInicio },
        { id: 'fechaFinal', etiqueta: 'Fecha Hasta', valor: this.fechaFinal },
      ];
    },
  },

  methods: {
    goBack() {
      window.history.back();
    },

    resetProgressData() {
      this.progressData = {
        progress: 0,
        processedRecords: 0,
        totalRecords: 0,
        estimatedTimeLeft: 0,
        statusMessage: 'Iniciando procesamiento...',
        statusType: 'info'
      };
      this.progressCancelled = false;
      this.sseJobActive = false;
    },

    async cancelProgress() {
      console.log('⛔ Cancelando proceso SSE...');

      // Marcar como cancelado para ignorar actualizaciones futuras
      this.progressCancelled = true;

      try {
        // Llamar al API para cancelar el job
        const cancelled = await reportesApiService.cancelJob();

        if (cancelled) {
          console.log('✅ Job cancelado exitosamente');
          showSimpleMessage('Proceso cancelado exitosamente', 'info');
        } else {
          console.log('⚠️ No se pudo cancelar el job');
          showSimpleMessage('No se pudo cancelar el proceso', 'warning');
        }
      } catch (error) {
        console.error('❌ Error al cancelar:', error);
        showSimpleMessage('Error al cancelar el proceso', 'error');
      } finally {
        // Detener loading
        this.isLoading = false;

        // Cerrar el diálogo y resetear datos
        this.showProgressDialog = false;
        this.resetProgressData();
      }
    },
    async obtenerTiposDeServicio() {
      try {
        const tiposServicios = await reportesApiService.obtenerTiposDeServicio(
          this.tiposOperatorias
        );
        this.tiposServicios = [...tiposServicios];
      } catch (error) {
        console.error('Error al obtener tipos de servicio', error);
      }
    },

    async obtenerEstadosSolicitud() {
      try {
        this.solicitudEstados =
          await reportesApiService.obtenerEstadosSolicitud(
            this.estadosPermitidos
          );
      } catch (error) {
        console.error('Error al obtener los estados de solicitud:', error);
      }
    },

    removeFilter(id) {
      // Prevenir eliminación de filtros requeridos para reportes especiales
      if (this.reporteEspecialActivo === 'Info Mora (Financiamiento)') {
        if (id === 'tipoServicio' || id === 'estadoSolicitud') {
          showSimpleMessage(
            'Filtro Requerido',
            'No puede eliminar este filtro mientras esté seleccionado "Info Mora (Financiamiento)"',
            'warning',
            3000
          );
          return;
        }
      }

      if (this.reporteEspecialActivo === 'Info Cobranzas Proyectadas') {
        if (id === 'tipoServicio' || id === 'estadoSolicitud') {
          showSimpleMessage(
            'Filtro Requerido',
            'No puede eliminar este filtro mientras esté seleccionado "Info Cobranzas Proyectadas"',
            'warning',
            3000
          );
          return;
        }
      }

      if (this.reporteEspecialActivo === 'Info Cobranzas') {
        if (id === 'tipoServicio' || id === 'estadoSolicitud') {
          showSimpleMessage(
            'Filtro Requerido',
            'No puede eliminar este filtro mientras esté seleccionado "Info Cobranzas"',
            'warning',
            3000
          );
          return;
        }
      }

      if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
        if (id === 'tipoServicio' || id === 'estadoSolicitud') {
          showSimpleMessage(
            'Filtro Requerido',
            'No puede eliminar este filtro mientras esté seleccionado "Info Cte y No Cte"',
            'warning',
            3000
          );
          return;
        }
      }

      if (this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
        if (id === 'tipoServicio' || id === 'estadoSolicitud') {
          showSimpleMessage(
            'Filtro Requerido',
            'No puede eliminar este filtro mientras esté seleccionado "Reporte de Saldos - HTC"',
            'warning',
            3000
          );
          return;
        }
      }

      switch (id) {
        case 'tipoServicio':
          // Si elimina tipo de servicio, limpiar selección
          this.filtroTipoServicio = null;
          this.getOperatoria(this.filtroFideicomiso, this.filtroTipoServicio);
          break;
        // case 'sectorProductivo':
        //   this.filtroSectorProductivo = null;
        //   break;
        case 'fideicomiso':
          // Si elimina fideicomiso, limpiar todas las selecciones
          this.filtroFideicomiso = [];
          this.getOperatoria(this.filtroFideicomiso, this.filtroTipoServicio);
          break;
        case 'operatoria':
          // Si elimina operatoria, limpiar todas las selecciones
          this.filtroOperatoria = [];
          this.listaParametros = [];
          this.selectedParams = [];
          break;
        case 'estadoSolicitud':
          this.estadoSeleccionado = [];
          break;
        case 'tipoPersona':
          this.filtroTipoPersona = null;
          break;
        case 'fechaInicio':
          this.fechaInicio = null;
          break;
        case 'fechaFinal':
          this.fechaFinal = null;
          break;
      }
    },

    limpiarFiltros() {
      const filtrosLimpios = ReportesHelpers.limpiarFiltrosHandler();
      Object.assign(this, filtrosLimpios);

      // Limpiar configuración especial
      this.reporteEspecialActivo = null;
      this.configuracionEspecial = null;

      // Resetear fecha de corte para Info Cte y No Cte
      this.fechaDisponibleCteYNoCte = null;

      // Restaurar operatorias al estado normal (sin filtrado especial)
      this.operatorias = [{ id: 'todas', nombre: 'Todas' }];

      // Asegurar que filtroFideicomiso vuelva a ser array después de limpiar
      this.filtroFideicomiso = [];

      // Ocultar la tabla del reporte
      this.mostrarTabla = false;

      // Habilitar filtros nuevamente
      this.reporteGenerado = false;

      // Limpiar la selección en el componente ReportSpecialSection
      this.$refs.reportSpecialSection &&
        this.$refs.reportSpecialSection.limpiarSeleccion();
    },

    // Métodos de gestión de cache
    limpiarCacheReportes() {
      const cleared = reportesCache.clearAll();
      showSimpleMessage(
        'Cache Limpiado',
        `Se eliminaron ${cleared} entradas del cache del navegador`,
        'success',
        3000
      );
    },

    verEstadisticasCache() {
      const stats = reportesCache.getStats();
      if (stats) {
        const mensaje = `Entradas totales: ${stats.totalEntries}\n` +
                       `Entradas válidas: ${stats.validEntries}\n` +
                       `Entradas expiradas: ${stats.expiredEntries}\n` +
                       `Tamaño total: ${stats.totalSizeKB} KB\n` +
                       `Tiempo de expiración: ${stats.maxAgeMinutes} minutos`;

        showSimpleMessage(
          'Estadísticas de Cache',
          mensaje,
          'info',
          5000
        );
      }
    },

    handleTipoServicioChange() {
      console.log('🔄 Cambiando tipo de operatoria:', this.filtroTipoServicio);
      console.log('🏦 Fideicomiso actual:', this.filtroFideicomiso);

      // Limpiar solo filtros dependientes, MANTENER fideicomiso
      this.filtroOperatoria = [];
      this.estadoSeleccionado = [];

      // Resetear operatorias disponibles y forzar actualización
      this.operatorias = [{ id: 'todas', nombre: 'Todas' }];

      // Ocultar tabla actual
      this.mostrarTabla = false;

      // Usar siempre el flujo estándar para cargar operatorias
      this.$nextTick(() => {
        console.log('✅ Filtros limpiados, obteniendo nuevas operatorias...');
        this.getOperatoria(this.filtroFideicomiso, this.filtroTipoServicio);
      });
    },

    async handleFideicomisoChange() {
      console.log('🔄 handleFideicomisoChange ejecutado');
      console.log('📊 Reporte especial activo:', this.reporteEspecialActivo);
      console.log('🏦 Fideicomiso seleccionado:', this.filtroFideicomiso);
      console.log(
        '🔧 Tipo de filtroFideicomiso:',
        typeof this.filtroFideicomiso
      );

      // Validaciones especiales para reportes que requieren fideicomiso único
      if (
        this.reporteEspecialActivo === 'Info Mora (Financiamiento)' ||
        this.reporteEspecialActivo === 'Info Cobranzas Proyectadas' ||
        this.reporteEspecialActivo === 'Info Cobranzas' ||
        this.reporteEspecialActivo === 'Info Cte y No Cte' ||
        this.reporteEspecialActivo === 'Reporte de Saldos - HTC'
      ) {
        console.log(
          '✅ Es reporte de fideicomiso único - ejecutando lógica especial'
        );

        // Para reportes de fideicomiso único, el filtroFideicomiso debe ser un string o null
        let fideicomisoSeleccionado = this.filtroFideicomiso;

        console.log(
          '🔍 Fideicomiso seleccionado (modo único):',
          fideicomisoSeleccionado
        );

        // No permitir "Todas" para reportes de fideicomiso único
        if (fideicomisoSeleccionado === 'Todas') {
          this.filtroFideicomiso = null;
          const tipoReporte = this.reporteEspecialActivo;
          showSimpleMessage(
            'Selección Requerida',
            `Para ${tipoReporte} debe seleccionar un fideicomiso específico, no "Todas"`,
            'warning',
            3000
          );
          return;
        }

        // Cargar operatorias específicas para el fideicomiso seleccionado
        if (fideicomisoSeleccionado && fideicomisoSeleccionado !== null) {
          console.log(
            '🚀 Cargando operatorias para fideicomiso:',
            fideicomisoSeleccionado
          );
          await this.cargarOperatoriasPorFideicomiso(fideicomisoSeleccionado);
        } else {
          // Si no hay fideicomiso seleccionado, limpiar operatorias
          console.log(
            '🧹 Limpiando operatorias - no hay fideicomiso seleccionado'
          );
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
          this.filtroOperatoria = [];
        }
      } else {
        console.log('📝 Es reporte normal - ejecutando lógica estándar');

        // Asegurar que filtroFideicomiso sea un array para reportes normales
        if (!Array.isArray(this.filtroFideicomiso)) {
          this.filtroFideicomiso = this.filtroFideicomiso
            ? [this.filtroFideicomiso]
            : [];
        }

        // Para reportes normales, usar la lógica existente
        this.getOperatoria(this.filtroFideicomiso, this.filtroTipoServicio);
      }
    },

    removeFideicomiso(fideicomisoNombre) {
      const index = this.filtroFideicomiso.indexOf(fideicomisoNombre);
      if (index > -1) {
        this.filtroFideicomiso.splice(index, 1);
        this.getOperatoria(this.filtroFideicomiso, this.filtroTipoServicio);
      }
    },

    truncarTexto(texto, maxLength) {
      return ReportesHelpers.truncarTexto(texto, maxLength);
    },

    formatearFecha(fecha) {
      return ReportesHelpers.formatearFecha(fecha);
    },

    handleOperatoriaChange() {
      // Si se selecciona "todas" junto con otras opciones, quitar las otras
      if (
        this.filtroOperatoria.includes('Todas') &&
        this.filtroOperatoria.length > 1
      ) {
        this.filtroOperatoria = ['Todas'];
      }

      // Llamar a getparametros con la primera operatoria seleccionada (para mantener compatibilidad)
      if (
        this.filtroOperatoria.length > 0 &&
        this.filtroOperatoria[0] !== 'Todas'
      ) {
        this.getparametros(this.filtroOperatoria[0]);
      }
    },

    handleEstadoChange() {
      // Si se selecciona "todas" junto con otras opciones, quitar las otras
      if (
        this.estadoSeleccionado.includes('todas') &&
        this.estadoSeleccionado.length > 1
      ) {
        this.estadoSeleccionado = ['todas'];
      }
    },

    handleDepartamentoChange(departamentos) {
      this.filtroDepartamento = departamentos;
      console.log('📍 Departamentos seleccionados:', this.filtroDepartamento);
    },

    removeOperatoria(operatoriaNombre) {
      const index = this.filtroOperatoria.indexOf(operatoriaNombre);
      if (index > -1) {
        this.filtroOperatoria.splice(index, 1);
        // Actualizar parámetros si hay operatorias restantes
        if (
          this.filtroOperatoria.length > 0 &&
          this.filtroOperatoria[0] !== 'Todas'
        ) {
          this.getparametros(this.filtroOperatoria[0]);
        } else {
          this.listaParametros = [];
          this.selectedParams = [];
        }
      }
    },

    removeEstado(estadoNombre) {
      // Encontrar el estado por nombre y removerlo por ID
      const estado = this.solicitudEstados.find(
        (e) => e.nombre === estadoNombre
      );
      if (estado) {
        const index = this.estadoSeleccionado.indexOf(estado.id);
        if (index > -1) {
          this.estadoSeleccionado.splice(index, 1);
        }
      }
    },
    async getFideicomiso() {
      try {
        this.fideicomisos = await reportesApiService.obtenerFideicomisos();
      } catch (error) {
        console.error('Error al obtener fideicomisos:', error);
      }
    },
    async obtenerDepartamentos() {
      try {
        const response = await reportesApiService.obtenerDepartamentos();
        this.departamentos = Array.isArray(response) ? response : [];
        console.log('📍 Departamentos cargados:', this.departamentos);
      } catch (error) {
        console.error('Error al obtener departamentos:', error);
        this.departamentos = [];
      }
    },
    async getOperatoria(fideicomisoFilt, tiposServiciosFilt) {
      if (!tiposServiciosFilt) {
        this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
        this.filtroOperatoria = [];
        return;
      }

      try {
        console.log('🔍 Obteniendo operatorias para tipo:', tiposServiciosFilt);
        // Limpiar operatorias existentes antes de obtener nuevas
        this.operatorias = [{ id: 'todas', nombre: 'Todas' }];

        const nuevasOperatorias = await reportesApiService.obtenerOperatorias(
          fideicomisoFilt,
          tiposServiciosFilt,
          this.page,
          this.limit,
          this.fideicomisos
        );

        // Asignar las nuevas operatorias
        this.operatorias = nuevasOperatorias;
        this.filtroOperatoria = [];

        
     
        
      } catch (error) {
        console.error('Error al obtener operatorias:', error);
        this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
        this.filtroOperatoria = [];
      }
    },
    async getparametros(operatoriaFilt) {
      this.listaParametros = [];
      this.selectedParams = [];

      const tipoServicio = this.operatorias.filter((el) => {
        if (el.nombre === `${operatoriaFilt}`) return el.id;
      });

      const tipoServicioId =
        tipoServicio[0] && tipoServicio[0].tipo_servicio_id;
      if (!tipoServicioId) {
        return;
      }

      try {
        this.listaParametros = await reportesApiService.obtenerParametros(
          tipoServicioId
        );
      } catch (error) {
        console.error('Error al obtener parámetros:', error);
      }
    },

    async getListaOperatorias() {
      try {
        this.listaOperatorias =
          await reportesApiService.obtenerListaOperatorias();
      } catch (error) {
        console.error('Error al obtener lista de operatorias:', error);
      }
    },

    async cargarOperatoriasPorFideicomiso(fideicomisoNombre) {
      try {
        // Obtener el ID del fideicomiso basado en su nombre
        const fideicomisoObj = this.fideicomisos.find(
          (fid) => fid.nombre === fideicomisoNombre
        );

        if (!fideicomisoObj || !fideicomisoObj.id) {
          console.error('No se encontró el fideicomiso:', fideicomisoNombre);
          return;
        }

        this.isLoading = true;
        console.log(
          '🔍 Cargando operatorias para fideicomiso:',
          fideicomisoNombre,
          'ID:',
          fideicomisoObj.id
        );

        const response =
          await reportesApiService.obtenerOperatoriasPorFideicomiso(
            fideicomisoObj.id
          );

        console.log(
          '🔍 Respuesta API obtenerOperatoriasPorFideicomiso:',
          response
        );

        // La respuesta viene en response.listado, no en response.lineaCreditos
        const operatorias =
          response && response.listado ? response.listado : [];

        if (operatorias.length > 0) {
          // Para Info Mora, filtrar solo operatorias de tipo Financiamiento (tipo_servicio_id: 13)
          let operatoriasFiltradas = operatorias;
          if (this.reporteEspecialActivo === 'Info Mora (Financiamiento)') {
            operatoriasFiltradas = operatorias.filter(
              (op) => op.tipo_servicio_id === 13
            );
            console.log(
              '🎯 Operatorias filtradas para Financiamiento:',
              operatoriasFiltradas.length
            );
          }

          // Para Info Cte y No Cte, filtrar solo operatorias activas de tipo Financiamiento
          if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
            console.log('🔍 Debug: Estructura de operatorias antes del filtro:', operatorias.slice(0, 3));
            console.log('🔍 Debug: Campos disponibles en primera operatoria:', Object.keys(operatorias[0] || {}));

            // Primero filtrar por tipo de servicio
            const operatoriasTipoFinanciamiento = operatorias.filter(
              (op) => op.tipo_servicio_id === 13
            );
            console.log('🔍 Operatorias tipo Financiamiento:', operatoriasTipoFinanciamiento.length);

            // Luego intentar filtrar por activo con diferentes valores posibles
            operatoriasFiltradas = operatoriasTipoFinanciamiento.filter(
              (op) => {
                const esActivo = op.activo === true || op.activo === 1 || op.activo === '1' ||
                               op.estado === 'activo' || op.estado === 'Activo' ||
                               op.status === 'active' || op.status === 1 ||
                               !op.hasOwnProperty('activo'); // Si no tiene campo activo, asumir que está activo
                console.log('🔍 Operatoria:', op.nombre, 'activo:', op.activo, 'estado:', op.estado, 'status:', op.status, 'esActivo:', esActivo);
                return esActivo;
              }
            );

            // Si no se encontraron operatorias activas, usar todas las de tipo Financiamiento temporalmente
            if (operatoriasFiltradas.length === 0) {
              console.log('⚠️ No se encontraron operatorias activas, usando todas las de tipo Financiamiento');
              operatoriasFiltradas = operatoriasTipoFinanciamiento;
            }
            console.log(
              '🎯 Operatorias filtradas para Info Cte y No Cte (solo activas):',
              operatoriasFiltradas.length
            );
          }

          // Para Reporte de Saldos - HTC, filtrar solo operatorias de tipo Financiamiento
          if (this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
            operatoriasFiltradas = operatorias.filter(
              (op) => op.tipo_servicio_id === 13
            );
            console.log(
              '🎯 Operatorias filtradas para Reporte de Saldos - HTC:',
              operatoriasFiltradas.length
            );
          }

          // Mapear las operatorias al formato esperado
          const operatoriasMapeadas = operatoriasFiltradas.map((linea) => ({
            id: linea.id,
            nombre: linea.nombre,
            tipo_servicio_id: linea.tipo_servicio_id,
            activo: linea.activo,
          }));

          this.operatorias = [
            { id: 'todas', nombre: 'Todas' },
            ...operatoriasMapeadas,
          ];

          console.log(
            '✅ Operatorias cargadas para informe especial:',
            operatoriasMapeadas.length
          );

          // Limpiar selección de operatoria al cambiar fideicomiso
          this.filtroOperatoria = [];
        } else {
          // No hay operatorias para este fideicomiso
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
          this.filtroOperatoria = [];

          let mensaje = `No se encontraron operatorias para el fideicomiso ${fideicomisoNombre}`;

          if (this.reporteEspecialActivo === 'Info Mora (Financiamiento)') {
            mensaje = `No se encontraron operatorias de Financiamiento para el fideicomiso ${fideicomisoNombre}`;
          } else if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
            mensaje = `No se encontraron operatorias activas de Financiamiento para el fideicomiso ${fideicomisoNombre}`;
          } else if (this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
            mensaje = `No se encontraron operatorias de Financiamiento para el fideicomiso ${fideicomisoNombre}`;
          }

          showSimpleMessage('Sin Operatorias', mensaje, 'info', 2000);
        }
      } catch (error) {
        console.error('Error al cargar operatorias por fideicomiso:', error);
        this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
        this.filtroOperatoria = [];

        showSimpleMessage(
          'Error',
          'Error al cargar las operatorias para el fideicomiso seleccionado',
          'error',
          3000
        );
      } finally {
        this.isLoading = false;
      }
    },

    getNombreFiltro(id) {
      const filtro = this.filtrosActivos.find((f) => f.id === id);
      return filtro ? filtro.nombre : '';
    },

    async obtenerDatosReportes(page = 1, limit = 10) {
      const filtros = {
        page,
        limit,
        estadoSeleccionado: this.estadoSeleccionado,
        filtroOperatoria: this.filtroOperatoria,
        operatorias: this.operatorias,
        filtroFideicomiso: this.filtroFideicomiso,
        fideicomisos: this.fideicomisos,
        filtroTipoServicio: this.filtroTipoServicio,
        fechaInicio: this.fechaInicio,
        fechaFinal: this.fechaFinal,
        filtroTipoPersona: this.filtroTipoPersona,
        filtroDepartamento: this.filtroDepartamento,
        departamentos: this.departamentos,
        mostrarSubtotales: this.mostrarSubtotales,
        esOperacion: this.esOperacion,
      };

      const params = ReportesHelpers.construirParametrosReporte(filtros);

      try {
        const resultado = await reportesApiService.obtenerDatosReportes(params);

        console.log('🔍 Reporte Normal - Respuesta completa:', resultado);
        if (
          resultado.datos.resultados &&
          resultado.datos.resultados.length > 0
        ) {
          console.log(
            '🔍 Reporte Normal - Primer item:',
            resultado.datos.resultados[0]
          );
          console.log(
            '🔍 Reporte Normal - Campos disponibles:',
            Object.keys(resultado.datos.resultados[0])
          );
        }

        // Mapear datos para aplanar la estructura anidada
        const datosAplanados = this.aplanarDatosReporte(resultado.datos);

        this.tablaMockeada = datosAplanados;
        this.paginaActual = page;
        this.itemsPorPagina = limit;
        this.totalItems = resultado.totalItems;
      } catch (error) {
        console.error('Error al obtener datos de reportes:', error);
        throw error;
      }
    },

    async activarTabla() {
      if (
        !ReportesHelpers.validarGeneracionReporte(
          this.filtroTipoServicio,
          this.filtroOperatoria
        )
      ) {
        showSimpleMessage(
          'Validación',
          'Debe seleccionar un tipo de operatoria o una operatoria específica para generar el reporte',
          'warning',
          3000
        );
        return;
      }

      // Resetear paginación al generar un nuevo reporte
      this.paginaActual = 1;
      this.itemsPorPagina = 10;

      this.isLoading = true;
      try {
        await this.obtenerDatosReportes(this.paginaActual, this.itemsPorPagina);
        this.mostrarTabla = true;
        // Deshabilitar filtros después de generar el reporte
        this.reporteGenerado = true;
      } catch (error) {
        console.error('Error al obtener datos del reporte:', error);
        showSimpleMessage(
          'Error',
          'No se pudieron cargar los datos del reporte',
          'error',
          3000
        );
      } finally {
        this.isLoading = false;
      }
    },

    async cambiarPagina(nuevaPagina) {
      this.paginaActual = nuevaPagina;
      this.isLoading = true;

      // Debug para Info Cobranzas Proyectadas durante paginación
      if (this.reporteEspecialActivo === 'Info Cobranzas Proyectadas') {
        console.log('🔄 PAGINACIÓN Info Cobranzas - Estado actual de filtros:', {
          paginaActual: this.paginaActual,
          nuevaPagina: nuevaPagina,
          filtroOperatoria: this.filtroOperatoria,
          filtroFideicomiso: this.filtroFideicomiso,
          operatorias: this.operatorias && this.operatorias.map ? this.operatorias.map(op => op.nombre) : 'no disponible',
          reporteEspecialActivo: this.reporteEspecialActivo
        });
      }

      try {
        if (this.reporteEspecialActivo === 'Info Mora (Financiamiento)') {
          await this.generarInfoMoraFinanciamiento(false);
        } else if (this.reporteEspecialActivo === 'Info Cobranzas Proyectadas') {
          await this.generarInfoCobranzasProyectadas(false);
        } else if (this.reporteEspecialActivo === 'Info Cobranzas') {
          await this.generarInfoCobranzas(false);
        } else if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
          await this.generarInfoCteYNoCte(false);
        } else if (this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
          await this.generarSaldosHTC(false);
        } else {
          await this.obtenerDatosReportes(
            this.paginaActual,
            this.itemsPorPagina
          );
        }
        this.mostrarTabla = true;
      } catch (error) {
        console.error('Error al cambiar página:', error);
        showSimpleMessage(
          'Error',
          'No se pudo cargar la página solicitada',
          'error',
          3000
        );
      } finally {
        this.isLoading = false;
      }
    },

    async cambiarItemsPorPagina(nuevoLimit) {
      // Manejar el caso especial de "Todos" (-1)
      if (nuevoLimit === -1) {
        // Mantener -1 para "Todos" para que el backend maneje la consulta directa
        this.itemsPorPagina = -1;
      } else {
        this.itemsPorPagina = nuevoLimit;
      }

      this.paginaActual = 1; // Reset to first page when changing items per page
      this.isLoading = true;
      try {
        if (this.reporteEspecialActivo === 'Info Mora (Financiamiento)') {
          await this.generarInfoMoraFinanciamiento(false);
        } else if (this.reporteEspecialActivo === 'Info Cobranzas Proyectadas') {
          await this.generarInfoCobranzasProyectadas(false);
        } else if (this.reporteEspecialActivo === 'Info Cobranzas') {
          await this.generarInfoCobranzas(false);
        } else if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
          await this.generarInfoCteYNoCte(false);
        } else if (this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
          await this.generarSaldosHTC(false);
        } else {
          await this.obtenerDatosReportes(
            this.paginaActual,
            this.itemsPorPagina
          );
        }
        this.mostrarTabla = true;
      } catch (error) {
        console.error('Error al cambiar items por página:', error);
        showSimpleMessage(
          'Error',
          'No se pudo actualizar el número de elementos por página',
          'error',
          3000
        );
      } finally {
        this.isLoading = false;
      }
    },

    async manejarExportacion(tipoExportacion) {
      console.log('🚀 Iniciando exportación:', tipoExportacion);

      // Resetear flags de control
      this.exportacionCancelada = false;
      this.exportacionEnSegundoPlano = false;
      this.isExportingInBackground = false;

      // Configurar loading según tipo de exportación
      if (tipoExportacion === 'excel') {
        this.isExportingExcel = true; // Loading en botón Excel
        this.isExporting = true; // Deshabilitar PDF mientras exporta Excel
        this.loadingTitle = 'Exportando a Excel';
        this.loadingMessage = 'Preparando exportación...';
        this.showLoadingDialog = true;
      } else if (tipoExportacion === 'pdf') {
        this.isExporting = true; // Deshabilitar ambos botones
        this.loadingTitle = 'Exportando a PDF';
        this.loadingMessage = 'Preparando exportación...';
        this.showLoadingDialog = true;
      }

      // Dar tiempo para que el dialog se muestre
      await this.$nextTick();

      this.isLoading = true;
      try {
        // Actualizar mensaje: obteniendo datos
        this.loadingMessage = 'Obteniendo todos los datos del servidor...';
        console.log('📊 Obteniendo todos los datos para exportación...');

        // Obtener todos los datos sin paginación
        const todosLosDatos = await this.obtenerTodosLosDatos();

        // Verificar si fue cancelado durante la obtención de datos
        if (this.exportacionCancelada) {
          console.log('⏭️ Exportación cancelada por el usuario');
          showSimpleMessage(
            'Cancelado',
            'La exportación fue cancelada',
            'info',
            3000
          );
          return;
        }

        console.log('✅ Datos obtenidos:', todosLosDatos.length, 'registros');

        // Actualizar mensaje: generando archivo
        if (tipoExportacion === 'excel') {
          this.loadingMessage = `Generando archivo Excel con ${todosLosDatos.length} registros...`;
        } else {
          this.loadingMessage = `Generando archivo PDF con ${todosLosDatos.length} registros...`;
        }

        console.log('📄 Generando archivo...');

        // Dar tiempo para actualizar el mensaje
        await this.$nextTick();

        // Pequeño delay para que el usuario vea el mensaje actualizado
        await new Promise(resolve => setTimeout(resolve, 300));

        // Verificar nuevamente si fue cancelado antes de generar
        if (this.exportacionCancelada) {
          console.log('⏭️ Exportación cancelada por el usuario antes de generar archivo');
          showSimpleMessage(
            'Cancelado',
            'La exportación fue cancelada',
            'info',
            3000
          );
          return;
        }

        if (tipoExportacion === 'pdf') {
          this.$refs.tablaReportes.generarPDF(todosLosDatos);
        } else if (tipoExportacion === 'excel') {
          this.$refs.tablaReportes.generarExcel(todosLosDatos);
        }

        console.log('✅ Archivo generado exitosamente');

        // Mostrar mensaje de éxito solo si no está en segundo plano
        if (!this.exportacionEnSegundoPlano) {
          showSimpleMessage(
            'Éxito',
            `Archivo ${tipoExportacion.toUpperCase()} generado correctamente`,
            'success',
            2000
          );
        } else {
          // Si está en segundo plano, mostrar mensaje diferente
          showSimpleMessage(
            'Descarga Completada',
            `El archivo ${tipoExportacion.toUpperCase()} se ha descargado exitosamente`,
            'success',
            3000
          );
        }
      } catch (error) {
        console.error('❌ Error al exportar datos:', error);

        // Solo mostrar error si no fue cancelado
        if (!this.exportacionCancelada) {
          showSimpleMessage(
            'Error',
            'No se pudieron exportar los datos. Por favor, intente nuevamente.',
            'error',
            3000
          );
        }
      } finally {
        this.isLoading = false;
        this.isExporting = false; // Rehabilitar botones de exportación
        this.isExportingExcel = false; // Quitar loading de botón Excel
        this.isExportingInBackground = false; // Quitar overlay de segundo plano Excel
        this.isExportingPdfInBackground = false; // Quitar overlay de segundo plano PDF

        // Cerrar loading dialog (ya se cerró en los métodos cancelar/background)
        // pero asegurar que esté cerrado al finalizar
        if (this.showLoadingDialog) {
          this.showLoadingDialog = false;
        }

        // Asegurar que la barra de progreso se cierre al finalizar (si estaba abierta)
        if (this.showProgressDialog && !this.progressCancelled) {
          this.showProgressDialog = false;
        }
      }
    },

    cancelarExportacion() {
      console.log('🛑 Usuario canceló la exportación');
      this.exportacionCancelada = true;
      this.isExporting = false; // Rehabilitar botones
      this.isExportingExcel = false; // Quitar loading de botón Excel
      this.isExportingInBackground = false; // Quitar overlay Excel
      this.isExportingPdfInBackground = false; // Quitar overlay PDF
      this.showLoadingDialog = false;

      showSimpleMessage(
        'Exportación Cancelada',
        'La exportación ha sido cancelada',
        'info',
        2000
      );
    },

    continuarEnSegundoPlano() {
      console.log('📥 Exportación continuará en segundo plano');
      this.exportacionEnSegundoPlano = true;
      this.showLoadingDialog = false;

      // Desactivar todos los loading globales para habilitar la UI
      this.isLoading = false; // IMPORTANTE: Habilitar tabla, filtros, etc.
      this.isExporting = false; // Habilitar botones de exportación
      this.isExportingExcel = false; // Quitar el loading del botón Excel

      // Activar overlay visual sobre AMBOS botones (Excel y PDF)
      this.isExportingInBackground = true; // Activar overlay de segundo plano Excel
      this.isExportingPdfInBackground = true; // Activar overlay de segundo plano PDF

      showSimpleMessage(
        'Segundo Plano',
        'La exportación continuará en segundo plano. El archivo se descargará automáticamente cuando esté listo.',
        'info',
        4000
      );
    },

    async obtenerTodosLosDatos() {
      // Para evitar timeouts 504, usar paginación automática
      const limitPorPagina = 100; // Pedir 100 registros por vez (más granular para mejor feedback)

      console.log('📊 obtenerTodosLosDatos - Tipo de reporte:', this.reporteEspecialActivo || 'Normal');

      const filtrosBase = {
        estadoSeleccionado: this.estadoSeleccionado,
        filtroOperatoria: this.filtroOperatoria,
        operatorias: this.operatorias,
        filtroFideicomiso: this.filtroFideicomiso,
        fideicomisos: this.fideicomisos,
        filtroTipoServicio: this.filtroTipoServicio,
        fechaInicio: this.fechaInicio,
        fechaFinal: this.fechaFinal,
        filtroTipoPersona: this.filtroTipoPersona,
        filtroDepartamento: this.filtroDepartamento,
        departamentos: this.departamentos,
        mostrarSubtotales: this.mostrarSubtotales,
        esOperacion: this.esOperacion,
        reporteEspecialActivo: this.reporteEspecialActivo, // Necesario para identificar Info Cte y No Cte
        fechaDisponibleSaldosHTC: this.fechaDisponibleSaldosHTC, // Fecha de corte para Saldos HTC
        fechaDisponibleCteYNoCte: this.fechaDisponibleCteYNoCte, // Fecha de corte para Info Cte y No Cte
      };

      try {
        // Para todos los reportes, usar limit: -1 para traer TODOS los registros
        // Esto es necesario para las exportaciones a Excel/PDF
        const filtros = {
          ...filtrosBase,
          page: 1,
          limit: -1,  // ✅ Traer todos los registros sin límite
        };

        const params = ReportesHelpers.construirParametrosReporte(filtros);

        console.log('🔍 obtenerTodosLosDatos - Parámetros construidos:', params);

        let datos = [];

        if (this.reporteEspecialActivo === 'Info Mora (Financiamiento)') {
          const resultado = await reportesApiService.obtenerInfoMoraFinanciamiento(params);
          datos = resultado.datos.resultados || [];
        } else if (this.reporteEspecialActivo === 'Info Cobranzas Proyectadas') {
          const resultado = await reportesApiService.obtenerInfoCobranzasProyectadas(params);
          const datosOriginales = resultado.datos.resultados || [];
          // Procesar los datos para crear las propiedades dinámicas antes de exportar
          datos = this.procesarDatosCobranzasProyectadas(datosOriginales);
          console.log(`✅ Info Cobranzas Proyectadas - Datos procesados: ${datos.length} registros`);
        } else if (this.reporteEspecialActivo === 'Info Cobranzas') {
          const resultado = await reportesApiService.obtenerInfoCobranzas(params, null);
          const datosOriginales = (resultado.response && resultado.response.data) ||
                                 (resultado.datos && resultado.datos.resultados) || [];
          // Procesar los datos para crear las propiedades dinámicas antes de exportar
          datos = this.procesarDatosCobranzas(datosOriginales);
          console.log(`✅ Info Cobranzas - Datos procesados: ${datos.length} registros`);
        } else if (this.reporteEspecialActivo === 'Info Cte y No Cte') {
          const resultado = await reportesApiService.obtenerInfoCteYNoCte(params);
          datos = (resultado.response && resultado.response.data) ||
                  (resultado.datos && resultado.datos.resultados) || [];
        } else if (this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
          const resultado = await reportesApiService.obtenerSaldosHTC(params);
          datos = (resultado.response && resultado.response.data) ||
                  (resultado.datos && resultado.datos.resultados) || [];
          // Actualizar el periodo si viene en el resultado
          if (resultado.periodo) {
            this.periodoSaldosHTC = resultado.periodo;
          }
          console.log(`✅ Saldos HTC - Datos obtenidos para exportación: ${datos.length} registros`);
          console.log(`✅ Saldos HTC - Periodo: ${this.periodoSaldosHTC}`);
        } else {
          // Reportes normales
          const resultado = await reportesApiService.obtenerDatosReportes(params);
          datos = resultado.datos.resultados || resultado.datos || [];
        }

        console.log('obtenerTodosLosDatos - Total registros:', datos.length);
        return datos;
      } catch (error) {
        console.error('Error al obtener todos los datos:', error);
        throw error;
      }
    },

    async informe(tipo) {
      if (!this.filtroOperatoria) {
        return showSimpleMessage(
          'Informe',
          'Debe seleccionar una operatoria',
          'info',
          2000
        );
      }

      this.isLoading = true;

      try {
        const operatoria = this.operatorias.find(
          (el) => el.nombre === this.filtroOperatoria
        );

        const parametrosInforme = {
          operatoriaId: operatoria.id,
          desde: this.fechaInicio || '',
          hasta: this.fechaFinal || '',
          parametros: this.selectedParams,
        };

        const data = await reportesApiService.obtenerInformeOperatoria(
          parametrosInforme
        );

        if (data.status === 'success' && data.creditos.length > 0) {
          const { datos, columnsParams } =
            reportesExportService.procesarDatosInforme(
              data.creditos,
              this.listaParametros,
              this.selectedParams
            );

          this.informes =
            reportesExportService.formatearDatosParaInforme(datos);
          this.informesPDF = reportesExportService.formatearDatosParaPDF(datos);

          this.exportPdf(tipo, columnsParams);
        } else {
          this.informes = [];
          return showSimpleMessage(
            'Informe',
            data.message || 'No se encontraron datos para el informe',
            'error',
            2000
          );
        }
      } catch (err) {
        console.error('Error en informe:', err);
        return showSimpleMessage(
          'Informe',
          'Error al obtener los datos. Por favor, verifica tu conexión a internet e intenta nuevamente.',
          'error',
          2000
        );
      } finally {
        this.isLoading = false;
      }
    },
    exportPdf(tipo, columnsParams = []) {
      if (!this.informes || this.informes.length === 0) {
        return 0;
      }

      if (tipo === 0 || tipo === 1) {
        reportesExportService.generarPDF(
          this.informesPDF,
          columnsParams,
          this.filtroOperatoria,
          this.fechaInicio,
          this.fechaFinal,
          tipo
        );

        if (tipo === 1) {
          this.fechaInicio = '';
          this.fechaFinal = '';
        }
      }

      if (tipo === 2) {
        reportesExportService.generarExcel(
          this.informes,
          columnsParams,
          this.filtroOperatoria
        );
      }

      this.limpiar();
    },
    limpiar() {
      this.isLoading = false;
      this.fideicomisos = [];
      this.operatorias = [];
      this.operatoria = '';
      this.fechaInicio = '';
      this.fechaFinal = '';
      this.filtroOperatoria = '';
      this.filtroFideicomiso = '';
      this.listaParametros = [];
      this.selectedParams = [];
      this.getFideicomiso();
    },

    async generarReporteEspecial(reporteEspecial) {
      console.log('🚀 generarReporteEspecial llamado con:', reporteEspecial);
      console.log(
        '🏦 Estado filtroFideicomiso al generar:',
        this.filtroFideicomiso
      );

      if (!reporteEspecial) {
        return showSimpleMessage(
          'Reporte Especial',
          'Debe seleccionar un tipo de reporte',
          'info',
          2000
        );
      }

      // Resetear paginación al generar un nuevo reporte
      this.paginaActual = 1;
      this.itemsPorPagina = 10;

      this.isLoading = true;
      try {
        if (reporteEspecial === 'Info Mora (Financiamiento)') {
          await this.generarInfoMoraFinanciamiento();
        } else if (reporteEspecial === 'Info Cobranzas Proyectadas') {
          await this.generarInfoCobranzasProyectadas();
        } else if (reporteEspecial === 'Info Cobranzas') {
          await this.generarInfoCobranzas();
        } else if (reporteEspecial === 'Info Cte y No Cte') {
          await this.generarInfoCteYNoCte();
        } else if (reporteEspecial === 'Reporte de Saldos - HTC') {
          await this.generarSaldosHTC();
        } else {
          showSimpleMessage(
            'Reporte Especial',
            `Generando ${reporteEspecial}...`,
            'success',
            2000
          );

          // Simular llamada a API para otros reportes
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error('Error al generar reporte especial:', error);
        showSimpleMessage(
          'Error',
          'No se pudo generar el reporte especial',
          'error',
          3000
        );
      } finally {
        this.isLoading = false;
      }
    },

    async generarInfoMoraFinanciamiento(mostrarMensaje = true) {
      try {
        const resultado = await reportesEspecialesService.generarInfoMora(
          this,
          this.aplanarDatosReporte,
          mostrarMensaje
        );

        if (resultado) {
          console.log('🔍 INFO MORA - RESULTADO FINAL tablaMockeada.resultados[0]:', resultado.tablaMockeada && resultado.tablaMockeada.resultados && resultado.tablaMockeada.resultados[0]);
          console.log('🔍 INFO MORA - departamento:', resultado.tablaMockeada && resultado.tablaMockeada.resultados && resultado.tablaMockeada.resultados[0] && resultado.tablaMockeada.resultados[0].departamento);
          this.tablaMockeada = resultado.tablaMockeada;
          this.totalItems = resultado.totalItems;
          this.reporteEspecialActivo = resultado.reporteEspecialActivo;
          this.mostrarTabla = true;
          this.reporteGenerado = true;
        }
      } catch (error) {
        this.mostrarTabla = false;
        this.reporteEspecialActivo = null;
      }
    },

    async generarInfoCobranzasProyectadas(mostrarMensaje = true) {
      try {
        const resultado = await reportesEspecialesService.generarInfoCobranzasProyectadas(
          this,
          this.procesarDatosCobranzasProyectadas,
          mostrarMensaje
        );

        if (resultado) {
          this.tablaMockeada = resultado.tablaMockeada;
          this.totalItems = resultado.totalItems;
          this.reporteEspecialActivo = resultado.reporteEspecialActivo;
          this.mostrarTabla = true;
          this.reporteGenerado = true;
        }
      } catch (error) {
        this.mostrarTabla = false;
        this.reporteEspecialActivo = null;
      }
    },

    async generarInfoCobranzas(mostrarMensaje = true) {
      try {
        const resultado = await reportesEspecialesService.generarInfoCobranzas(
          this,
          this.procesarDatosCobranzas,
          mostrarMensaje
        );

        if (resultado) {
          this.tablaMockeada = resultado.tablaMockeada;
          this.totalItems = resultado.totalItems;
          this.reporteEspecialActivo = resultado.reporteEspecialActivo;
          this.mostrarTabla = true;
          this.reporteGenerado = true;
        }
      } catch (error) {
        this.mostrarTabla = false;
        this.reporteEspecialActivo = null;
      }
    },

    async generarInfoCteYNoCte(mostrarMensaje = true) {
      try {
        const resultado = await reportesEspecialesService.generarInfoCteYNoCte(
          this,
          mostrarMensaje
        );

        if (resultado) {
          this.tablaMockeada = resultado.tablaMockeada;
          this.totalItems = resultado.totalItems;
          this.reporteEspecialActivo = resultado.reporteEspecialActivo;
          this.fechaDisponibleCteYNoCte = resultado.fechaDisponibleCteYNoCte;
          this.mostrarTabla = true;
          this.reporteGenerado = true;
        }
      } catch (error) {
        this.mostrarTabla = false;
        this.reporteEspecialActivo = null;
      }
    },

    async generarSaldosHTC(mostrarMensaje = true) {
      try {
        const resultado = await reportesEspecialesService.generarSaldosHTC(
          this,
          mostrarMensaje
        );

        if (resultado) {
          this.tablaMockeada = resultado.tablaMockeada;
          this.totalItems = resultado.totalItems;
          this.reporteEspecialActivo = resultado.reporteEspecialActivo;
          this.fechaDisponibleSaldosHTC = resultado.fechaDisponibleSaldosHTC;
          this.periodoSaldosHTC = resultado.periodoSaldosHTC; // Guardar periodo
          this.mostrarTabla = true;
          this.reporteGenerado = true;
        }
      } catch (error) {
        this.mostrarTabla = false;
        this.reporteEspecialActivo = null;
      }
    },

    procesarDatosCobranzas(datos) {
      console.log('🔄 Procesando datos de Info Cobranzas:', datos);

      return datos.map(item => {
        // Crear una copia del item original
        const itemProcesado = { ...item };

        // Procesar cuotas_detalle si existe
        if (item.cuotas_detalle && Array.isArray(item.cuotas_detalle)) {
          item.cuotas_detalle.forEach(cuota => {
            // Para Info Cobranzas, usar fecimputa que ya viene en formato YYYY-MM
            if (cuota.fecimputa && cuota.cobrado && cuota.cobrado > 0) {
              try {
                // Crear clave usando el formato periodo_YYYY-MM
                const claveColumna = `periodo_${cuota.fecimputa}`;

                // Si la columna ya existe, sumar (para múltiples cuotas del mismo período)
                // Si no existe, inicializar
                itemProcesado[claveColumna] = (itemProcesado[claveColumna] || 0) + (parseFloat(cuota.cobrado) || 0);
                console.log(`📅 Asignando ${cuota.cobrado} a columna ${claveColumna} para legajo ${item.legajo}. Total: ${itemProcesado[claveColumna]}`);
              } catch (error) {
                console.error('Error al procesar periodo:', cuota.fecimputa, error);
              }
            }
          });
        }

        return itemProcesado;
      });
    },

    procesarDatosCobranzasProyectadas(datos) {
      console.log('🔄 Procesando datos de Cobranzas Proyectadas:', datos);

      return datos.map(item => {
        // Crear una copia del item original
        const itemProcesado = { ...item };

        // Procesar cuotas_detalle si existe
        if (item.cuotas_detalle && Array.isArray(item.cuotas_detalle)) {
          // NOTA: El backend ya filtra por rango de fechas, no es necesario filtrar nuevamente aquí
          item.cuotas_detalle.forEach(cuota => {
            if (cuota.fechaVencimiento && cuota.montoCuota) {
              try {
                // Parsear la fecha de vencimiento
                const fecha = new Date(cuota.fechaVencimiento);

                const mes = fecha.getMonth() + 1; // getMonth() devuelve 0-11, necesitamos 1-12
                const año = fecha.getFullYear();

                // Crear clave única que incluye mes y año
                const claveColumna = `fecha_${mes}_${año}`;

                // Asignar el monto a la columna correspondiente
                if (mes >= 1 && mes <= 12) {
                  // Si la columna ya existe, sumar (para múltiples cuotas del mismo mes-año)
                  // Si no existe, inicializar
                  itemProcesado[claveColumna] = (itemProcesado[claveColumna] || 0) + (parseFloat(cuota.montoCuota) || 0);
                  console.log(`📅 Asignando ${cuota.montoCuota} a columna ${claveColumna} para legajo ${item.legajo}. Total: ${itemProcesado[claveColumna]}`);
                }
              } catch (error) {
                console.error('Error al procesar fecha de vencimiento:', cuota.fechaVencimiento, error);
              }
            }
          });
        }

        return itemProcesado;
      });
    },

    async configurarFiltrosEspeciales(configuracion) {
      console.log('🔧 configurarFiltrosEspeciales llamado durante:', {
        esPaginacion: 'Verificar si viene de paginación',
        configuracion: configuracion,
        reporteActual: this.reporteEspecialActivo,
        stackTrace: new Error().stack.split('\n').slice(1, 4)
      });
      
      // Evitar reconfigurar si ya está activo el mismo reporte (caso paginación)
      if (configuracion && configuracion.tipoReporte === this.reporteEspecialActivo) {
        console.log('🔧 Saltando reconfiguración - mismo reporte ya activo');
        return;
      }
      
      if (!configuracion) {
        // Limpiar configuración especial
        this.reporteEspecialActivo = null;
        this.configuracionEspecial = null;
        return;
      }

      this.reporteEspecialActivo = configuracion.tipoReporte;
      this.configuracionEspecial = configuracion.configuracion;

      if (configuracion.tipoReporte === 'Info Mora (Financiamiento)') {
        // Buscar el ID del tipo de servicio "Financiamiento"
        const tipoFinanciamiento = this.tiposServicios.find(
          (ts) => ts.nombre === 'Financiamiento'
        );

        if (tipoFinanciamiento) {
          this.filtroTipoServicio = tipoFinanciamiento.id;
        }

        // Buscar los IDs de los estados "Otorgado" y "Cancelado"
        const estadoOtorgado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Otorgado'
        );
        const estadoCancelado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Cancelado'
        );

        // Establecer ambos estados: Otorgado (7) y Cancelado (22)
        const estadosRequeridos = [];
        if (estadoOtorgado) {
          estadosRequeridos.push(estadoOtorgado.id);
        }
        if (estadoCancelado) {
          estadosRequeridos.push(estadoCancelado.id);
        }

        if (estadosRequeridos.length > 0) {
          this.estadoSeleccionado = estadosRequeridos;
        }

        // Para Info Mora, inicializar filtroFideicomiso como string (null para selección única)
        // Si ya había un array con un elemento, conservar ese elemento
        if (
          Array.isArray(this.filtroFideicomiso) &&
          this.filtroFideicomiso.length === 1
        ) {
          const fideicomisoSeleccionado = this.filtroFideicomiso[0];
          this.filtroFideicomiso = fideicomisoSeleccionado;
          await this.cargarOperatoriasPorFideicomiso(fideicomisoSeleccionado);
        } else if (
          typeof this.filtroFideicomiso === 'string' &&
          this.filtroFideicomiso
        ) {
          // Si ya hay un fideicomiso seleccionado como string, mantenerlo y cargar operatorias
          console.log(
            '🔄 Manteniendo fideicomiso ya seleccionado:',
            this.filtroFideicomiso
          );
          await this.cargarOperatoriasPorFideicomiso(this.filtroFideicomiso);
        } else {
          // Limpiar y establecer como null para selección única
          this.filtroFideicomiso = null;
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
          this.filtroOperatoria = [];
        }

        // Desactivar subtotales para Info Mora
        if (configuracion.configuracion.deshabilitarSubtotales) {
          this.mostrarSubtotales = false;
        }

        // Hide table when configuring filters to prevent automatic loading
        this.mostrarTabla = false;

        console.log(
          '✅ Configuración aplicada para Info Mora (Financiamiento)'
        );
        console.log(
          '🔧 filtroFideicomiso establecido como:',
          this.filtroFideicomiso
        );
        console.log(
          '🔧 Tipo de filtroFideicomiso:',
          typeof this.filtroFideicomiso
        );

        // Forzar actualización del watcher en el componente hijo
        this.$nextTick(() => {
          console.log(
            '🔄 Forzando actualización del filtro en el componente hijo'
          );
          // Emitir un evento o forzar re-render para que el watcher se ejecute con isInfoMoraActive = true
        });
      }

      if (configuracion.tipoReporte === 'Info Cobranzas Proyectadas') {
        // Buscar el ID del tipo de servicio "Financiamiento"
        const tipoFinanciamiento = this.tiposServicios.find(
          (ts) => ts.nombre === 'Financiamiento'
        );

        if (tipoFinanciamiento) {
          this.filtroTipoServicio = tipoFinanciamiento.id;
        }

        // Buscar los IDs de los estados "Otorgado" y "Cancelado"
        const estadoOtorgado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Otorgado'
        );
        const estadoCancelado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Cancelado'
        );

        // Establecer ambos estados: Otorgado (7) y Cancelado (22)
        const estadosRequeridos = [];
        if (estadoOtorgado) {
          estadosRequeridos.push(estadoOtorgado.id);
        }
        if (estadoCancelado) {
          estadosRequeridos.push(estadoCancelado.id);
        }

        if (estadosRequeridos.length > 0) {
          this.estadoSeleccionado = estadosRequeridos;
        }

        // Para Info Cobranzas Proyectadas, inicializar filtroFideicomiso como string (null para selección única)
        // Si ya había un array con un elemento, conservar ese elemento
        if (
          Array.isArray(this.filtroFideicomiso) &&
          this.filtroFideicomiso.length === 1
        ) {
          const fideicomisoSeleccionado = this.filtroFideicomiso[0];
          this.filtroFideicomiso = fideicomisoSeleccionado;
          await this.cargarOperatoriasPorFideicomiso(fideicomisoSeleccionado);
        } else if (
          typeof this.filtroFideicomiso === 'string' &&
          this.filtroFideicomiso
        ) {
          // Si ya hay un fideicomiso seleccionado como string, mantenerlo y cargar operatorias
          console.log(
            '🔄 Manteniendo fideicomiso ya seleccionado:',
            this.filtroFideicomiso
          );
          await this.cargarOperatoriasPorFideicomiso(this.filtroFideicomiso);
        } else {
          // Limpiar y establecer como null para selección única
          this.filtroFideicomiso = null;
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
          this.filtroOperatoria = [];
        }

        // Establecer fecha desde como hoy
        const hoy = new Date();
        this.fechaInicio = hoy.toISOString().substr(0, 10); // formato YYYY-MM-DD

        // Hide table when configuring filters to prevent automatic loading
        this.mostrarTabla = false;

        console.log(
          '✅ Configuración aplicada para Info Cobranzas Proyectadas'
        );
        console.log(
          '🔧 filtroTipoServicio establecido como:',
          this.filtroTipoServicio
        );
        console.log(
          '🔧 estadoSeleccionado establecido como:',
          this.estadoSeleccionado
        );
        console.log('🔧 fechaInicio establecida como:', this.fechaInicio);
      }

      if (configuracion.tipoReporte === 'Info Cobranzas') {
        // Buscar el ID del tipo de servicio "Financiamiento"
        const tipoFinanciamiento = this.tiposServicios.find(
          (ts) => ts.nombre === 'Financiamiento'
        );

        if (tipoFinanciamiento) {
          this.filtroTipoServicio = tipoFinanciamiento.id;
        }

        // Buscar los IDs de los estados "Otorgado" y "Cancelado"
        const estadoOtorgado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Otorgado'
        );
        const estadoCancelado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Cancelado'
        );

        // Establecer ambos estados: Otorgado (7) y Cancelado (22)
        const estadosRequeridos = [];
        if (estadoOtorgado) {
          estadosRequeridos.push(estadoOtorgado.id);
        }
        if (estadoCancelado) {
          estadosRequeridos.push(estadoCancelado.id);
        }

        if (estadosRequeridos.length > 0) {
          this.estadoSeleccionado = estadosRequeridos;
        }

        // Para Info Cobranzas, inicializar filtroFideicomiso como string (null para selección única)
        // Si ya había un array con un elemento, conservar ese elemento
        if (
          Array.isArray(this.filtroFideicomiso) &&
          this.filtroFideicomiso.length === 1
        ) {
          const fideicomisoSeleccionado = this.filtroFideicomiso[0];
          this.filtroFideicomiso = fideicomisoSeleccionado;
          await this.cargarOperatoriasPorFideicomiso(fideicomisoSeleccionado);
        } else if (
          typeof this.filtroFideicomiso === 'string' &&
          this.filtroFideicomiso
        ) {
          // Si ya hay un fideicomiso seleccionado como string, mantenerlo y cargar operatorias
          console.log(
            '🔄 Manteniendo fideicomiso ya seleccionado:',
            this.filtroFideicomiso
          );
          await this.cargarOperatoriasPorFideicomiso(this.filtroFideicomiso);
        } else {
          // Limpiar y establecer como null para selección única
          this.filtroFideicomiso = null;
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
          this.filtroOperatoria = [];
        }

        // Establecer fecha hasta como hoy
        const hoy = new Date();
        this.fechaFinal = hoy.toISOString().substr(0, 10); // formato YYYY-MM-DD

        // Desactivar subtotales para Info Cobranzas
        if (configuracion.configuracion.deshabilitarSubtotales) {
          this.mostrarSubtotales = false;
        }

        // Hide table when configuring filters to prevent automatic loading
        this.mostrarTabla = false;

        console.log(
          '✅ Configuración aplicada para Info Cobranzas'
        );
        console.log(
          '🔧 filtroTipoServicio establecido como:',
          this.filtroTipoServicio
        );
        console.log(
          '🔧 estadoSeleccionado establecido como:',
          this.estadoSeleccionado
        );
        console.log(
          '🔧 fechaFinal establecida como:',
          this.fechaFinal
        );
      }

      if (configuracion.tipoReporte === 'Info Cte y No Cte') {
        // Buscar el ID del tipo de servicio "Financiamiento"
        const tipoFinanciamiento = this.tiposServicios.find(
          (ts) => ts.nombre === 'Financiamiento'
        );

        if (tipoFinanciamiento) {
          this.filtroTipoServicio = tipoFinanciamiento.id;
        }

        // Buscar los IDs de los estados "Otorgado" y "Cancelado"
        const estadoOtorgado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Otorgado'
        );
        const estadoCancelado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Cancelado'
        );

        // Establecer ambos estados: Otorgado (7) y Cancelado (22)
        const estadosRequeridos = [];
        if (estadoOtorgado) {
          estadosRequeridos.push(estadoOtorgado.id);
        }
        if (estadoCancelado) {
          estadosRequeridos.push(estadoCancelado.id);
        }

        if (estadosRequeridos.length > 0) {
          this.estadoSeleccionado = estadosRequeridos;
        }

        // Para Info Cte y No Cte, inicializar filtroFideicomiso como string (null para selección única)
        // Si ya había un array con un elemento, conservar ese elemento
        if (
          Array.isArray(this.filtroFideicomiso) &&
          this.filtroFideicomiso.length === 1
        ) {
          const fideicomisoSeleccionado = this.filtroFideicomiso[0];
          this.filtroFideicomiso = fideicomisoSeleccionado;
          await this.cargarOperatoriasPorFideicomiso(fideicomisoSeleccionado);
        } else if (
          typeof this.filtroFideicomiso === 'string' &&
          this.filtroFideicomiso
        ) {
          // Si ya hay un fideicomiso seleccionado como string, mantenerlo y cargar operatorias
          console.log(
            '🔄 Manteniendo fideicomiso ya seleccionado:',
            this.filtroFideicomiso
          );
          await this.cargarOperatoriasPorFideicomiso(this.filtroFideicomiso);
        } else {
          // Limpiar y establecer como null para selección única
          this.filtroFideicomiso = null;
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
          this.filtroOperatoria = [];
        }

        // No activar subtotales automáticamente para Info Cte y No Cte (se manejan en backend)
        this.mostrarSubtotales = false;

        // Establecer fecha de corte como fecha actual para Info Cte y No Cte
        const hoy = new Date();
        this.fechaFinal = hoy.toISOString().substr(0, 10); // formato YYYY-MM-DD

        // Hide table when configuring filters to prevent automatic loading
        this.mostrarTabla = false;

        console.log(
          '✅ Configuración aplicada para Info Cte y No Cte'
        );
        console.log(
          '🔧 filtroTipoServicio establecido como:',
          this.filtroTipoServicio
        );
        console.log(
          '🔧 estadoSeleccionado establecido como:',
          this.estadoSeleccionado
        );
        console.log(
          '🔧 mostrarSubtotales establecido como:',
          this.mostrarSubtotales
        );
      }

      if (configuracion.tipoReporte === 'Reporte de Saldos - HTC') {
        // Buscar el ID del tipo de servicio "Financiamiento"
        const tipoFinanciamiento = this.tiposServicios.find(
          (ts) => ts.nombre === 'Financiamiento'
        );

        if (tipoFinanciamiento) {
          this.filtroTipoServicio = tipoFinanciamiento.id;
        }

        // Buscar los IDs de los estados "Otorgado" y "Cancelado"
        const estadoOtorgado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Otorgado'
        );
        const estadoCancelado = this.solicitudEstados.find(
          (estado) => estado.nombre === 'Cancelado'
        );

        // Establecer ambos estados: Otorgado (7) y Cancelado (22)
        const estadosRequeridos = [];
        if (estadoOtorgado) {
          estadosRequeridos.push(estadoOtorgado.id);
        }
        if (estadoCancelado) {
          estadosRequeridos.push(estadoCancelado.id);
        }

        if (estadosRequeridos.length > 0) {
          this.estadoSeleccionado = estadosRequeridos;
        }

        // Para Reporte de Saldos - HTC, inicializar filtroFideicomiso como string (null para selección única)
        // Si ya había un array con un elemento, conservar ese elemento
        if (
          Array.isArray(this.filtroFideicomiso) &&
          this.filtroFideicomiso.length === 1
        ) {
          const fideicomisoSeleccionado = this.filtroFideicomiso[0];
          this.filtroFideicomiso = fideicomisoSeleccionado;
          await this.cargarOperatoriasPorFideicomiso(fideicomisoSeleccionado);
        } else if (
          typeof this.filtroFideicomiso === 'string' &&
          this.filtroFideicomiso
        ) {
        
          await this.cargarOperatoriasPorFideicomiso(this.filtroFideicomiso);
        } else {
          // Limpiar y establecer como null para selección única
          this.filtroFideicomiso = null;
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
          this.filtroOperatoria = [];
        }

        // No activar subtotales automáticamente para Reporte de Saldos - HTC
        this.mostrarSubtotales = false;

        // Establecer fecha de corte como fecha actual para Reporte de Saldos - HTC
        const hoy = new Date();
        this.fechaFinal = hoy.toISOString().substr(0, 10); // formato YYYY-MM-DD

        // Hide table when configuring filters to prevent automatic loading
        this.mostrarTabla = false;

      }
    },

    handleSubtotalesChange(value) {
      this.mostrarSubtotales = value;
    },

    async cargarOperatoriasFiltradas() {
      try {
        // Obtener el ID del fideicomiso seleccionado
        const fideicomisoSeleccionado = Array.isArray(this.filtroFideicomiso)
          ? this.filtroFideicomiso[0]
          : this.filtroFideicomiso;

        const fideicomiso = this.fideicomisos.find(
          (f) => f.nombre === fideicomisoSeleccionado
        );

        if (!fideicomiso) {
          console.warn(
            '🚫 No se encontró el fideicomiso:',
            fideicomisoSeleccionado
          );
          return;
        }

        console.log(
          '📡 Llamando al endpoint con fideicomiso_id:',
          fideicomiso.id,
          'tipo_servicio_id:',
          this.filtroTipoServicio
        );

        // Usar axios directamente para llamar al endpoint específico
        const { default: axios } = await import('axios');
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}api/v1/lineas-creditos/filter`,
          {
            params: {
              fideicomiso_id: fideicomiso.id,
              tipo_servicio_id: this.filtroTipoServicio,
            },
            headers: {
              'Content-Type': 'application/json',
              token: localStorage.getItem('token'),
            },
          }
        );

        console.log('📋 Operatorias filtradas recibidas:', response.data);

        if (response.data.status === 'success' && response.data.response.data) {
          // Mapear los datos al formato esperado
          const operatoriasMapeadas = response.data.response.data.map((op) => ({
            id: op.id,
            nombre: op.nombre,
          }));

          // Agregar "Todas" al principio y asignar
          this.operatorias = [
            { id: 'todas', nombre: 'Todas' },
            ...operatoriasMapeadas,
          ];

          console.log(
            '✅ Operatorias cargadas:',
            this.operatorias.length - 1,
            'operatorias'
          );
        } else {
          console.warn(
            '🚫 No se encontraron operatorias para los filtros seleccionados'
          );
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
        }
      } catch (error) {
        console.error('❌ Error al cargar operatorias filtradas:', error);
        this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
      }
    },

    aplanarDatosReporte(datos) {
      console.log('🔍 aplanarDatosReporte - reporteEspecialActivo:', this.reporteEspecialActivo);
      console.log('🔍 aplanarDatosReporte - primer item:', datos.resultados && datos.resultados[0]);

      // Si no hay resultados, devolver la estructura original
      if (!datos.resultados || !Array.isArray(datos.resultados)) {
        return datos;
      }

      // Para reportes especiales, NO expandir datos - mantener estructura original
      // PERO sí mapear departamento si es necesario
      if (this.reporteEspecialActivo === 'Info Cobranzas' ||
          this.reporteEspecialActivo === 'Info Cte y No Cte' ||
          this.reporteEspecialActivo === 'Info Mora (Financiamiento)' ||
          this.reporteEspecialActivo === 'Reporte de Saldos - HTC') {
        console.log('✅ Entrando en bloque especial de reportes');
        // Mapear departamento en los resultados si es necesario
        const resultadosConDepartamento = datos.resultados.map((item, index) => {
          if (index === 0) {
            console.log('🔍 Procesando primer item - departamento:', item.departamento, 'legajo:', item.legajo);
          }
          // Si el departamento ya viene del backend como string, no hacer nada
          if (item.departamento && item.departamento !== '') {
            if (index === 0) {
              console.log('✅ Departamento ya existe, retornando item sin cambios');
            }
            return item;
          }
          // Si no, intentar extraerlo de estructura anidada
          const itemConDepartamento = { ...item };
          if (item.localidad && typeof item.localidad === 'object' && item.localidad.departamento && item.localidad.departamento.nombre) {
            itemConDepartamento.departamento = item.localidad.departamento.nombre;
          } else if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
            itemConDepartamento.departamento = item.cliente.localidad.departamento.nombre;
          } else {
            itemConDepartamento.departamento = '-';
          }
          
          return itemConDepartamento;
        });
        
        return {
          ...datos,
          resultados: resultadosConDepartamento,
        };
      }

      // Para otros reportes, mapear cada item para aplanar la estructura anidada
      const resultadosAplanados = datos.resultados.map((item) => {
        const itemAplanado = { ...item };

        // Mapear ID a legajo
        if (item.id) {
          itemAplanado.legajo = item.id;
        }

        // Mapear campos del objeto cliente si existe
        if (item.cliente) {
          // Mapear razonSocial y nombre/apellido a nombre_razon_social
          if (item.cliente.razonSocial) {
            itemAplanado.nombre_razon_social = item.cliente.razonSocial.toUpperCase();
          } else if (item.cliente.nombre && item.cliente.apellido) {
            itemAplanado.nombre_razon_social = `${item.cliente.apellido}, ${item.cliente.nombre}`.toUpperCase();
          } else if (item.cliente.nombre) {
            itemAplanado.nombre_razon_social = item.cliente.nombre.toUpperCase();
          }

          // Mapear otros campos del cliente
          if (item.cliente.cuit) {
            itemAplanado.cuit = item.cliente.cuit;
          }

          // Mapear tipo de persona
          if (item.cliente.tipoCliente !== undefined) {
            itemAplanado.persona =
              item.cliente.tipoCliente === 0 ? 'Jurídica' : 'Física';
          }
        }

        // Mapear operatoria a operatoria_linea
        if (item.operatoria) {
          itemAplanado.operatoria_linea = item.operatoria;
        }

        // Mapear fideicomisoName a fideicomiso
        if (item.fideicomisoName) {
          itemAplanado.fideicomiso = item.fideicomisoName;
        }

        // Mapear fechaIngreso a fecha_ingreso (convertir timestamp si es necesario)
        if (item.fechaIngreso) {
          if (typeof item.fechaIngreso === 'number') {
            // Si es timestamp, convertir a fecha
            const fecha = new Date(item.fechaIngreso);
            itemAplanado.fecha_ingreso = fecha.toISOString().split('T')[0]; // YYYY-MM-DD
          } else {
            itemAplanado.fecha_ingreso = item.fechaIngreso;
          }
        }

        // Mapear fechaOtorgamiento si existe
        if (item.fechaOtorgamiento) {
          itemAplanado.fecha_otorgamiento = item.fechaOtorgamiento;
        }

        // Mapear departamento - priorizar si ya viene del backend como string
        if (!item.departamento || item.departamento === '') {
          // Si no tiene departamento o está vacío, intentar extraerlo de estructura anidada
          if (item.localidad && typeof item.localidad === 'object' && item.localidad.departamento && item.localidad.departamento.nombre) {
            itemAplanado.departamento = item.localidad.departamento.nombre;
          } else if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
            itemAplanado.departamento = item.cliente.localidad.departamento.nombre;
          } else {
            itemAplanado.departamento = '-';
          }
        }
        // Si item.departamento ya existe, se mantiene por el spread operator


     

        return itemAplanado;
      });

      // Devolver la estructura con los resultados aplanados
      return {
        ...datos,
        resultados: resultadosAplanados,
      };
    },

    expandirDatosInfoCobranzas(datos) {
      console.log('📊 Expandiendo datos para Info Cobranzas:', datos);

      const resultadosExpandidos = [];

      datos.resultados.forEach((item) => {
        // Datos base del registro
        const datosBase = {
          legajo: item.legajo,
          fideicomiso: item.fideicomiso,
          operatoria_linea: item.operatoria_linea,
          nombre_razon_social: item.nombre_razon_social.toUpperCase(),
          cuit: item.cuit,
          persona: item.persona,
          fecha_ingreso: item.fecha_ingreso,
          estado: item.estado,
          periodo: item.periodo,
        };

        // Si tiene cuotas_detalle, crear una fila por cada cuota
        if (item.cuotas_detalle && Array.isArray(item.cuotas_detalle)) {
          item.cuotas_detalle.forEach((cuota, index) => {
            resultadosExpandidos.push({
              ...datosBase,
              // Campos específicos de la cuota
              fecimputa: cuota.fecimputa, // La fecha del período
              cobrado: cuota.cobrado, // El monto cobrado en lugar de 'monto'
              fechaVencimiento: cuota.fechaVencimiento,
              montoCuota: cuota.montoCuota,
              // Identificador único para la fila
              _id: `${item.legajo}-${index}`,
              _esCuotaDetalle: true,
              _cuotaIndex: index,
            });
          });
        } else {
          // Si no tiene cuotas_detalle, agregar el registro tal como está
          resultadosExpandidos.push({
            ...datosBase,
            fecimputa: null,
            cobrado: 0,
            fechaVencimiento: null,
            montoCuota: 0,
            _id: item.legajo,
            _esCuotaDetalle: false,
          });
        }
      });

      console.log('📊 Datos expandidos Info Cobranzas:', {
        registrosOriginales: datos.resultados.length,
        registrosExpandidos: resultadosExpandidos.length,
        muestra: resultadosExpandidos.slice(0, 3)
      });

      return {
        ...datos,
        resultados: resultadosExpandidos,
      };
    },
  },
  async created() {
    await this.getFideicomiso();
  },
  async mounted() {
    await Promise.all([
      this.obtenerTiposDeServicio(),
      this.getFideicomiso(),
      this.obtenerEstadosSolicitud(),
      this.obtenerDepartamentos(),
    ]);
  },
  watch: {
    filtroTipoServicio(newVal, oldVal) {
      // Solo procesar si hay un cambio real y no es la inicialización
      if (oldVal !== undefined && newVal !== oldVal) {
        console.log('👁️ Watcher filtroTipoServicio:', oldVal, '->', newVal);
        // El manejo ya se hace en handleTipoServicioChange(),
        // aquí solo validamos que no se pierda la lógica
        if (!newVal) {
          // Si se limpia el tipo de servicio, limpiar operatorias
          this.operatorias = [{ id: 'todas', nombre: 'Todas' }];
          this.filtroOperatoria = [];
        }
      }
    },
    filtroFideicomiso() {
      // Resetear operatoria cuando cambia el fideicomiso
      this.filtroOperatoria = [];
    },
  },
};
</script>
<style>
.v-date-picker-table--date td {
  padding: 0px !important;
}

.custom-select {
  width: 150px; /* Ajusta el ancho según tus necesidades */
}
.custom-date-field {
  width: 150px; /* Ajusta el ancho según tus necesidades */
}
.my-1 {
  margin-bottom: 10px;
}
.transition-button {
  transition: all 0.3s ease;
}
.transition-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}
.resumen-card {
  min-height: auto !important;
  max-height: none !important;
  height: auto !important;
  flex-grow: 1;
  overflow: visible !important;
}

.resumen-filtros {
  table-layout: fixed; /* Layout fijo para mejor control */
  width: 100%;
  min-height: auto;
  height: auto;
}

.resumen-filtros td {
  white-space: normal !important;
  word-break: break-word;
  vertical-align: top !important;
  font-size: 35px;
  line-height: 1.4;
}

.fila-resumen {
  min-height: 45px; /* Altura mínima aún más reducida */
  vertical-align: top;
  margin-bottom: 8px !important; /* Espacio explícito entre filas */
}

.fila-resumen td {
  padding: 6px 8px !important; /* Padding aún más reducido */
  vertical-align: top !important;
}

.fila-resumen td:first-child {
  min-width: 120px; /* Ancho mínimo para la columna de títulos */
  width: 120px;
}

.fila-resumen td:last-child {
  width: auto; /* Ancho automático para el contenido */
  max-width: calc(100% - 130px); /* Máximo ancho dejando espacio para títulos */
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  max-height: 35px; /* Altura muy reducida para forzar scroll rápido */
  overflow-y: auto; /* Scroll vertical cuando excede la altura */
  overflow-x: hidden;
  width: 100%;
  padding: 2px 6px 2px 2px; /* Padding más compacto */
  min-height: 28px; /* Altura mínima reducida */
  border: 1px solid #e0e0e0; /* Borde sutil para definir el área */
  border-radius: 4px;
  background-color: #fafafa; /* Fondo sutil */
  margin-bottom: 4px; /* Margen inferior para separar del siguiente elemento */
}

.chips-container::-webkit-scrollbar {
  width: 8px;
}

.chips-container::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 4px;
  margin: 2px;
}

.chips-container::-webkit-scrollbar-thumb {
  background: #bdbdbd;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.chips-container::-webkit-scrollbar-thumb:hover {
  background: #9e9e9e;
}

.chips-container::-webkit-scrollbar-thumb:active {
  background: #757575;
}

.elipsis-text {
  display: inline-block;
  max-width: 220px; /* Ajustá este valor según tu diseño */
  white-space: nowrap; /* Impide saltos de línea */
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 18px;
}

.campo-label {
  color: #0b165f;
  font-size: 55px;
  white-space: nowrap !important; /* Evitar salto de línea en títulos */
  min-width: 120px; /* Ancho mínimo para los títulos */
  width: 120px; /* Ancho fijo para consistencia */
  padding-right: 8px;
}
.v-icon.text-error:hover {
  color: #c62828 !important;
  transform: scale(1.2);
  transition: all 0.2s ease;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
